import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronRightIcon, SearchIcon } from "@chakra-ui/icons";
import {
  FiActivity,
  FiClipboard,
  FiDroplet,
  FiEdit3,
  FiEye,
  FiFileText,
  FiHeart,
  FiPackage,
  FiThermometer,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { useDropzone } from "react-dropzone";
import { uploadFilesApi } from "../../api/files";
import FileList, { formatFileSize } from "../files/FileList";
import Injection from "./injection";
import Prescription from "./prescription";
import VitalSigns from "./vitalSigns";
import Payment from "./payment";
import Note from "./note";
import Parasitology from "./parasitology";
import ChemPathology from "./chemPathology";
import Urine from "./urine";
import Haematology from "./haematology";
import VisualSkill from "./visualSkill";
import PaymentSection, { emptyPayment } from "./PaymentSection";

const FORM_OPTIONS = [
  {
    type: "note",
    title: "Clinical note",
    description: "Add a short observation or care note",
    group: "Clinical",
    icon: FiEdit3,
  },
  {
    type: "vital",
    title: "Vital signs",
    description: "Record temperature, blood pressure and other observations",
    group: "Clinical",
    icon: FiActivity,
  },
  {
    type: "prescription",
    title: "Prescription",
    description: "Document medicines, dosage and instructions",
    group: "Treatment",
    icon: FiClipboard,
  },
  {
    type: "injection",
    title: "Injection",
    description: "Record injectable medication and administration details",
    group: "Treatment",
    icon: FiPackage,
  },
  {
    type: "haematology",
    title: "Haematology",
    description: "Request or enter blood investigation details",
    group: "Laboratory",
    icon: FiHeart,
  },
  {
    type: "chemPathology",
    title: "Chemical pathology",
    description: "Request or enter chemistry investigation details",
    group: "Laboratory",
    icon: FiThermometer,
  },
  {
    type: "parasitology",
    title: "Parasitology",
    description: "Request or enter parasitology investigation details",
    group: "Laboratory",
    icon: FiFileText,
  },
  {
    type: "urine",
    title: "Urinalysis",
    description: "Request or enter urine investigation details",
    group: "Laboratory",
    icon: FiDroplet,
  },
  {
    type: "visualSkill",
    title: "Visual assessment",
    description: "Record visual acuity and eye assessment findings",
    group: "Assessment",
    icon: FiEye,
  },
];

const FORM_COMPONENTS = {
  injection: Injection,
  prescription: Prescription,
  payment: Payment,
  vital: VitalSigns,
  note: Note,
  parasitology: Parasitology,
  chemPathology: ChemPathology,
  urine: Urine,
  haematology: Haematology,
  visualSkill: VisualSkill,
};

const patientName = (bioData) =>
  `${bioData?.surname || ""} ${bioData?.otherNames || ""}`.trim();

const Attachment = ({
  bioData,
  setAttachmentToEdit,
  attachmentToEdit,
  attachmentModal,
  onSubmitAttachment,
  mode = "create",
  isSubmitting = false,
}) => {
  const [step, setStep] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [payment, setPayment] = useState(emptyPayment);
  const formContainerRef = useRef(null);
  const toast = useToast();
  const selectedOption = FORM_OPTIONS.find((option) => option.type === step);
  const SelectedForm = FORM_COMPONENTS[step];

  useEffect(() => {
    if (attachmentToEdit?.type) {
      setStep(attachmentToEdit.type);
      setPayment({ ...emptyPayment, ...(attachmentToEdit.payment || {}) });
    }
  }, [attachmentToEdit]);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return FORM_OPTIONS;
    return FORM_OPTIONS.filter((option) =>
      `${option.title} ${option.description} ${option.group}`
        .toLowerCase()
        .includes(query),
    );
  }, [search]);

  const groupedOptions = filteredOptions.reduce((groups, option) => {
    groups[option.group] = [...(groups[option.group] || []), option];
    return groups;
  }, {});

  const close = () => {
    if (!isSubmitting && !uploading) attachmentModal?.onClose();
  };

  const reset = () => {
    setStep(null);
    setSearch("");
    if (setAttachmentToEdit) setAttachmentToEdit(null);
    setSelectedFiles([]);
    setPayment(emptyPayment);
  };

  const dropzone = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/webp": [],
      "application/pdf": [],
    },
    maxSize: 10 * 1024 * 1024,
    maxFiles: 5,
    onDrop: (accepted, rejected) => {
      if (rejected.length)
        toast({
          status: "error",
          title: "Some files were rejected",
          description:
            "Use up to five JPEG, PNG, WebP or PDF files, no larger than 10MB each.",
        });
      setSelectedFiles((current) => [...current, ...accepted].slice(0, 5));
    },
  });

  const submitWithFiles = async (data) => {
    try {
      setUploading(true);
      const response = selectedFiles.length
        ? await uploadFilesApi(selectedFiles, "clinical")
        : null;
      await onSubmitAttachment({
        ...data,
        payment: {
          ...payment,
          fullAmount: Number(payment.fullAmount) || 0,
          discount: Number(payment.discount) || 0,
          totalAmount: Number(payment.totalAmount) || 0,
        },
        fileIds: response?.data?.files?.map((file) => file._id) || [],
      });
    } catch (error) {
      toast({
        status: "error",
        title: "Unable to upload files",
        description: error.response?.data?.msg || error.message,
      });
    } finally {
      setUploading(false);
    }
  };

  const goBack = () => {
    if (attachmentToEdit) return;
    setStep(null);
  };

  const actionLabel =
    mode === "edit"
      ? "Edit form"
      : mode === "copy"
        ? "Copy form"
        : "Attach a form";
  const submitSelectedForm = () => {
    formContainerRef.current?.querySelector('button[type="submit"]')?.click();
  };

  return (
    <Modal
      isCentered
      size="6xl"
      scrollBehavior="inside"
      closeOnOverlayClick={!isSubmitting && !uploading}
      closeOnEsc={!isSubmitting && !uploading}
      onCloseComplete={reset}
      isOpen={attachmentModal?.isOpen}
      onClose={close}
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="2xl" overflow="hidden" maxH="92vh">
        {(isSubmitting || uploading) && (
          <Progress size="xs" isIndeterminate colorScheme="blue" />
        )}
        <ModalHeader borderBottomWidth="1px" px={{ base: "5", md: "7" }} py="5">
          <Flex align="center" gap="3" pr="9">
            {step && !attachmentToEdit && (
              <Center
                as="button"
                aria-label="Choose another form"
                type="button"
                w="36px"
                h="36px"
                borderRadius="lg"
                _hover={{ bg: "gray.100" }}
                onClick={goBack}
              >
                <ArrowBackIcon />
              </Center>
            )}
            <Box minW="0">
              <HStack flexWrap="wrap">
                <Heading size="md">
                  {selectedOption ? selectedOption.title : actionLabel}
                </Heading>
                {selectedOption && (
                  <Badge colorScheme="blue" borderRadius="full">
                    {selectedOption.group}
                  </Badge>
                )}
              </HStack>
              <Text mt="1" color="gray.500" fontSize="sm" fontWeight="normal">
                {selectedOption
                  ? `${mode === "edit" ? "Update" : mode === "copy" ? "Review and copy" : "Complete"} this form for ${patientName(bioData) || "the selected patient"}.`
                  : `Choose the form you want to complete${patientName(bioData) ? ` for ${patientName(bioData)}` : ""}.`}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton
          top={isSubmitting || uploading ? "6" : "5"}
          isDisabled={isSubmitting || uploading}
        />

        <ModalBody p="0" bg={step ? "white" : "gray.50"} overflowX="hidden">
          {SelectedForm ? (
            <Box
              ref={formContainerRef}
              px={{ base: "3", md: "6" }}
              py="3"
              w="full"
              minW="0"
              maxW="full"
              overflowX="hidden"
              opacity={isSubmitting || uploading ? 0.65 : 1}
              pointerEvents={isSubmitting || uploading ? "none" : "auto"}
              sx={{
                "> div > svg:first-of-type": { display: "none" },
                "button[type='submit']": { display: "none" },
                "form, form > div": { minWidth: 0, maxWidth: "100%" },
                "input, textarea, select": { minWidth: 0, maxWidth: "100%" },
              }}
            >
              <Box
                mb="4"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.300"
                borderRadius="xl"
                p="4"
                bg="gray.50"
                {...dropzone.getRootProps()}
                cursor="pointer"
              >
                <input {...dropzone.getInputProps()} />
                <HStack justify="space-between" align="center">
                  <HStack>
                    <Icon as={FiUploadCloud} color="brand.600" boxSize="5" />
                    <Box>
                      <Text fontWeight="700" fontSize="sm">
                        Optional supporting files
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        JPEG, PNG, WebP or PDF · up to 5 files · 10MB each
                      </Text>
                    </Box>
                  </HStack>
                  <Button size="sm" variant="outline">
                    Choose files
                  </Button>
                </HStack>
              </Box>
              {!!selectedFiles.length && (
                <VStack align="stretch" spacing="2" mb="4">
                  {selectedFiles.map((file, index) => (
                    <HStack
                      key={`${file.name}-${index}`}
                      bg="blue.50"
                      px="3"
                      py="2"
                      borderRadius="lg"
                    >
                      <Text flex="1" fontSize="sm" noOfLines={1}>
                        {file.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatFileSize(file.size)}
                      </Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        aria-label="Remove file"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedFiles((items) =>
                            items.filter((_, itemIndex) => itemIndex !== index),
                          );
                        }}
                      >
                        <FiX />
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              )}
              <FileList files={attachmentToEdit?.files || []} />
              <SelectedForm
                goBack={goBack}
                bioData={bioData}
                form={attachmentToEdit?.form}
                onSubmitAttachment={submitWithFiles}
                onPaymentChange={setPayment}
              />
              <PaymentSection
                value={payment}
                onChange={setPayment}
                readOnly={step === "prescription"}
                derivedFromItems={step === "prescription"}
              />
            </Box>
          ) : (
            <Box p={{ base: "5", md: "7" }}>
              <InputGroup maxW="560px" mx="auto" size="lg">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  bg="white"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search clinical, laboratory or treatment forms"
                />
              </InputGroup>

              {!filteredOptions.length ? (
                <Center minH="300px" flexDirection="column" textAlign="center">
                  <Icon as={FiFileText} boxSize="8" color="gray.400" />
                  <Heading mt="3" size="sm">
                    No matching forms
                  </Heading>
                  <Text mt="1" color="gray.500" fontSize="sm">
                    Try a form name or category such as laboratory.
                  </Text>
                </Center>
              ) : (
                <Stack mt="7" spacing="7">
                  {Object.entries(groupedOptions).map(([group, options]) => (
                    <Box key={group}>
                      <HStack mb="3">
                        <Text fontSize="sm" fontWeight="800" color="gray.600">
                          {group}
                        </Text>
                        <Divider />
                      </HStack>
                      <SimpleGrid columns={{ base: 1, md: 2 }} gap="3">
                        {options.map((option) => (
                          <Flex
                            as="button"
                            type="button"
                            key={option.type}
                            textAlign="left"
                            align="center"
                            gap="4"
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="xl"
                            p="4"
                            transition="all 0.15s ease"
                            onClick={() => setStep(option.type)}
                            _hover={{
                              borderColor: "brand.300",
                              boxShadow: "sm",
                              transform: "translateY(-1px)",
                            }}
                            _focusVisible={{ boxShadow: "outline" }}
                          >
                            <Center
                              flexShrink="0"
                              w="44px"
                              h="44px"
                              borderRadius="xl"
                              bg="brand.50"
                              color="brand.600"
                            >
                              <Icon as={option.icon} boxSize="5" />
                            </Center>
                            <Box flex="1" minW="0">
                              <Text fontWeight="800">{option.title}</Text>
                              <Text
                                mt="1"
                                color="gray.500"
                                fontSize="sm"
                                lineHeight="short"
                              >
                                {option.description}
                              </Text>
                            </Box>
                            <ChevronRightIcon color="gray.400" boxSize="5" />
                          </Flex>
                        ))}
                      </SimpleGrid>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </ModalBody>
        {SelectedForm && (
          <ModalFooter
            flexShrink="0"
            borderTopWidth="1px"
            bg="white"
            px={{ base: "4", md: "7" }}
            py="4"
          >
            <Button
              w="full"
              colorScheme="blue"
              isLoading={isSubmitting || uploading}
              loadingText={uploading ? "Uploading files" : "Saving form"}
              onClick={submitSelectedForm}
            >
              {mode === "edit"
                ? "Save changes"
                : mode === "copy"
                  ? "Copy form"
                  : "Attach form"}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default Attachment;
